import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const PrivacyPreferencesStep = ({ formData, onChange }) => {
  const handleCheckboxChange = (name, checked) => {
    onChange({ target: { name, value: checked } });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
          Privacy Preferences
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Control how your information is shared and used within the app.
        </p>
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start space-x-3">
          <Icon name="Lock" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-medium text-foreground mb-2">
              Your Privacy Matters
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground">
              These settings determine what information is visible to your partner and how the AI mediates your communications. You can change these settings anytime.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4 md:space-y-5">
        <div className="bg-card border border-border rounded-lg p-4 md:p-5">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
            Data Sharing with Partner
          </h3>
          <div className="space-y-4">
            <Checkbox
              label="Share mood tracking data"
              description="Allow your partner to see your general mood trends (not specific entries)"
              checked={formData?.shareMoodData}
              onChange={(e) => handleCheckboxChange('shareMoodData', e?.target?.checked)}
            />
            <Checkbox
              label="Share availability calendar"
              description="Let your partner view your available time slots for scheduling"
              checked={formData?.shareAvailability}
              onChange={(e) => handleCheckboxChange('shareAvailability', e?.target?.checked)}
            />
            <Checkbox
              label="Share encounter preferences"
              description="Allow AI to share your general preferences (not specific desires)"
              checked={formData?.sharePreferences}
              onChange={(e) => handleCheckboxChange('sharePreferences', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 md:p-5">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
            AI Communication Settings
          </h3>
          <div className="space-y-4">
            <Checkbox
              label="Enable AI mediation for desires"
              description="Let AI translate and communicate your intimate desires to your partner"
              checked={formData?.enableAIMediation}
              onChange={(e) => handleCheckboxChange('enableAIMediation', e?.target?.checked)}
            />
            <Checkbox
              label="Allow AI compatibility analysis"
              description="Enable AI to analyze compatibility and suggest optimal encounter times"
              checked={formData?.allowCompatibilityAnalysis}
              onChange={(e) => handleCheckboxChange('allowCompatibilityAnalysis', e?.target?.checked)}
            />
            <Checkbox
              label="Receive AI suggestions"
              description="Get personalized suggestions for improving intimate communication"
              checked={formData?.receiveAISuggestions}
              onChange={(e) => handleCheckboxChange('receiveAISuggestions', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 md:p-5">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
            Notification Preferences
          </h3>
          <div className="space-y-4">
            <Checkbox
              label="Partner mood updates"
              description="Get notified when your partner's mood changes significantly"
              checked={formData?.notifyMoodChanges}
              onChange={(e) => handleCheckboxChange('notifyMoodChanges', e?.target?.checked)}
            />
            <Checkbox
              label="Schedule reminders"
              description="Receive reminders about upcoming scheduled encounters"
              checked={formData?.notifyScheduleReminders}
              onChange={(e) => handleCheckboxChange('notifyScheduleReminders', e?.target?.checked)}
            />
            <Checkbox
              label="AI communication alerts"
              description="Get notified when AI has new messages or suggestions for you"
              checked={formData?.notifyAIMessages}
              onChange={(e) => handleCheckboxChange('notifyAIMessages', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-foreground">
              <strong>Note:</strong> Disabling all sharing options may limit the app's ability to help coordinate intimate encounters with your partner.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPreferencesStep;